'use client';
import React, { useImperativeHandle, useState } from 'react';
import Modal from '@/components/ui/Modal';
import useSharedRef from '@/hooks/useSharedRef';
import { toast } from 'react-toastify';
import { INITIAL_FORM_STATE, EMAIL_REGEX } from '@/constants/constants';
import ContactFormLeftPanel from './ContactFormLeftPanel';
import ContactFormRightPanel from './ContactFormRightPanel';

const ContactForm: React.FC = () => {
  const modalRef = useSharedRef('modalRef');
  const [open, setOpen] = useState<boolean>(false);
  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState<Partial<Record<keyof typeof INITIAL_FORM_STATE, string>>>({});

  const handleChange = (field: keyof typeof INITIAL_FORM_STATE) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      setForm(prev => ({ ...prev, [field]: e.target.value }));
      if (errors[field]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    };

  const resetForm = (email = '') => {
    setForm({ ...INITIAL_FORM_STATE, email });
    setErrors({});
  };

  useImperativeHandle(
    modalRef,
    () => ({
      open: (email: string) => {
        resetForm(email || '');
        setOpen(true);
      },
    }),
    [setOpen]
  );

  const validateForm = () => {
    let tempErrors: Partial<Record<keyof typeof INITIAL_FORM_STATE, string>> = {};
    let isValid = true;

    if (!form.name) {
      tempErrors.name = 'This field is mandatory';
      isValid = false;
    } else if (form.name.trim().length < 2) {
      tempErrors.name = 'Must be at least 2 characters long';
      isValid = false;
    }

    if (!form.company) {
      tempErrors.company = 'This field is mandatory';
      isValid = false;
    } else if (form.company.trim().length < 2) {
      tempErrors.company = 'Must be at least 2 characters long';
      isValid = false;
    }

    if (!form.email) {
      tempErrors.email = 'This field is mandatory';
      isValid = false;
    } else if (!EMAIL_REGEX.test(form.email)) {
      tempErrors.email = 'Provide a valid work email.';
      isValid = false;
    }

    if (!form.jobTitle) {
      tempErrors.jobTitle = 'This field is mandatory';
      isValid = false;
    } else if (form.jobTitle.trim().length < 2) {
      tempErrors.jobTitle = 'Must be at least 2 characters long';
      isValid = false;
    }

    if (!form.reason) {
      tempErrors.reason = 'This field is mandatory';
      isValid = false;
    } else if (form.reason.trim().length < 2) {
      tempErrors.reason = 'Must be at least 2 characters long';
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const submit: React.ComponentProps<'form'>['onSubmit'] = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      const payload = {
        name: form.name,
        company: form.company,
        email: form.email,
        jobTitle: form.jobTitle,
        reason: form.reason,
        location: form.location,
        phone: form.contactNo,
        message: form.message,
      };
      setOpen(false);
      fetch('https://contactus.sixsense.ai', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then(() => {
          toast.clearWaitingQueue();
          toast.dismiss();
          toast.success(
            'Thank you! Your message has been sent. The Sixsense team will reach out soon.'
          );
        })
        .catch(() => {
          toast.clearWaitingQueue();
          toast.dismiss();
          toast.error('Something went wrong. Please try again later.');
        });
    }
  };

  return (
    <Modal open={open} className="flex items-stretch justify-center max-w-[1100px] w-[95%] sm:w-[500px] md:w-[600px] lg:w-[95%]">
      <div className="flex w-full min-h-[auto] sm:max-h-[90dvh] lg:min-h-[640px] max-h-[95dvh] rounded-lg overflow-hidden shadow-2xl bg-white flex-col lg:flex-row">
        <ContactFormLeftPanel />
        <ContactFormRightPanel
          form={form}
          errors={errors}
          handleChange={handleChange}
          setForm={setForm}
          onClose={() => setOpen(false)}
          onSubmit={submit}
        />
      </div>
    </Modal>
  );
};

export default ContactForm;
